import { Link } from 'react-router-dom';
import { ArrowRight, Volume2, VolumeX, Wind } from 'lucide-react';
import BreathingCue from '@/components/BreathingCue';
import EXHALE from '@/lib/testIds';

const RECOGNITION_CARDS = [
  { label: 'Overthinking',      mode: 'slow_spiral_reset' },
  { label: 'Mental Clutter',    mode: 'mental_declutter' },
  { label: 'Stress',            mode: 'quiet_reset' },
  { label: 'Focus Recovery',    mode: 'focus_recovery' },
  { label: 'Emotional Release', mode: 'emotional_release' },
  { label: 'Midnight Thoughts', mode: 'midnight_thoughts' },
];

export default function Landing({ settings, onChangeSettings }) {
  const toggleSound = () => onChangeSettings({ ...settings, ambientSound: !settings.ambientSound });

  return (
    <>
      {/* ---------------- HERO ---------------- */}
      <section
        data-testid={EXHALE.landingRoot}
        className="relative min-h-[calc(100vh-6rem)] px-6 sm:px-12 pt-16 sm:pt-20 pb-12"
      >
        <BreathingCue active={settings.breathing} />

        <div className="relative z-10 mx-auto max-w-2xl">
          <p className="eyebrow animate-soft-in">a quiet place</p>

          <h1 className="mt-6 font-serif font-light text-4xl sm:text-6xl leading-[1.05] tracking-tight animate-soft-in">
            Type until your<br />
            mind feels <span className="italic text-sage">lighter</span>.
          </h1>

          <p className="mt-8 max-w-lg text-base sm:text-lg leading-relaxed text-foreground/75 animate-soft-in">
            A private space to unload thoughts, interrupt overthinking, and create
            a little more mental room.
          </p>

          {/* CTA stack — primary, secondary, tertiary */}
          <div className="mt-12 flex flex-col gap-7 animate-soft-in">
            <Link
              to="/session/free"
              data-testid={EXHALE.beginExhaleBtn}
              className="group inline-flex items-center justify-between gap-6 self-start rounded-full bg-charcoal pl-7 pr-6 py-4 text-sand shadow-[0_1px_0_rgba(0,0,0,0.04)] transition-all duration-500 hover:pl-8 hover:pr-5"
            >
              <span className="inline-flex items-center gap-3">
                <Wind size={18} strokeWidth={1.5} className="opacity-80" />
                <span className="text-base sm:text-lg">Begin an Exhale</span>
              </span>
              <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-500 group-hover:translate-x-1" />
            </Link>

            <div className="flex flex-wrap items-center gap-x-9 gap-y-3 text-base">
              <Link
                to="/session/brain_unload"
                data-testid={EXHALE.brainUnloadBtn}
                className="quiet-link opacity-80 hover:opacity-100"
              >
                Brain Unload
              </Link>
              <Link
                to="/session/midnight_thoughts"
                data-testid={EXHALE.midnightThoughtsBtn}
                className="quiet-link opacity-60 hover:opacity-100"
              >
                Midnight Thoughts
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* -------- EMOTIONAL RECOGNITION -------- */}
      <section
        data-testid={EXHALE.recognitionRoot}
        className="relative z-10 px-6 sm:px-12 pb-20 sm:pb-28"
      >
        <div className="mx-auto max-w-3xl">
          <p className="eyebrow">a small honest question</p>
          <h2 className="mt-4 max-w-xl font-serif font-light text-3xl sm:text-4xl tracking-tight leading-[1.15]">
            Maybe your mind is carrying<br className="hidden sm:block" /> too much right now.
          </h2>
          <p className="mt-4 max-w-lg text-foreground/65">
            Pick whatever is closest. You can change your mind in a few breaths.
          </p>

          <ul className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {RECOGNITION_CARDS.map((c) => (
              <li key={c.mode}>
                <Link
                  to={`/session/${c.mode}`}
                  data-testid={EXHALE.recognitionCard(c.mode)}
                  className="group block w-full rounded-2xl border border-border/70 bg-bone/40 px-6 py-6 text-left transition-all duration-500 hover:bg-bone hover:border-sage/60"
                >
                  <div className="flex items-center justify-between gap-4">
                    <span className="font-serif text-xl sm:text-[1.35rem] font-normal tracking-tight">
                      {c.label}
                    </span>
                    <ArrowRight
                      size={14}
                      strokeWidth={1.25}
                      className="opacity-40 transition-all duration-500 group-hover:opacity-100 group-hover:translate-x-1"
                    />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* -------------- STANZAS -------------- */}
      <section className="relative z-10 px-6 sm:px-12 pb-24">
        <div className="mx-auto grid max-w-3xl grid-cols-1 gap-10 sm:grid-cols-3">
          <Stanza label="01 — arrive" text="No login. No setup. Open it, and you're already inside." />
          <Stanza label="02 — type" text="Free-form, or pick a mode like Midnight Thoughts or Brain Unload." />
          <Stanza label="03 — exhale" text="When you're done, the page closes around your words like a quiet drawer." />
        </div>
      </section>

      {/* -------------- QUIET FOOTER -------------- */}
      <section className="relative z-10 px-6 sm:px-12 pb-16">
        <div className="mx-auto flex max-w-3xl items-center justify-between text-sm">
          <Link
            to="/history"
            data-testid={EXHALE.openHistoryBtn}
            className="quiet-link opacity-70 hover:opacity-100"
          >
            past exhales
          </Link>
          <button
            type="button"
            onClick={toggleSound}
            data-testid={EXHALE.soundToggle}
            className="quiet-link inline-flex items-center gap-2 opacity-70 hover:opacity-100"
          >
            {settings.ambientSound
              ? (<><Volume2 size={14} strokeWidth={1.25} /> sound on</>)
              : (<><VolumeX size={14} strokeWidth={1.25} /> sound off</>)}
          </button>
        </div>
      </section>
    </>
  );
}

function Stanza({ label, text }) {
  return (
    <div>
      <p className="eyebrow">{label}</p>
      <p className="mt-3 text-base leading-relaxed text-foreground/80">{text}</p>
    </div>
  );
}
